import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SignatureCanvas from 'react-signature-canvas';
import { formatDateInput, convertToISO, displayDateValue } from '../utils/dateUtils';
import { useOrganization } from '../contexts/OrganizationContext';

function Page6({ formData, updateFormData, onSubmit, onBack, isSubmitting }) {
  const { t } = useTranslation();
  const { organization } = useOrganization();
  const signatureRef = useRef(null);
  const [uploadedFiles, setUploadedFiles] = useState(formData.uploadedFiles || []);

  // Auto-fill today's date when page loads (if not already set)
  React.useEffect(() => {
    if (!formData.signatureDate) {
      const today = new Date();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const year = today.getFullYear();
      const todayFormatted = `${month}/${day}/${year}`;
      updateFormData({ signatureDate: todayFormatted });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    updateFormData({ [name]: value });
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    const formatted = formatDateInput(value);
    
    // Store the formatted value for display
    // Only convert to ISO when complete (10 characters)
    if (formatted.length === 10) {
      const iso = convertToISO(formatted);
      updateFormData({ [name]: iso });
    } else {
      // Store the partial formatted value temporarily
      updateFormData({ [name]: formatted });
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate file sizes (max 10MB per file)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    const oversizedFiles = files.filter(file => file.size > maxSize);
    
    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map(f => `${f.name} (${(f.size / 1024 / 1024).toFixed(2)}MB)`).join(', ');
      alert(`The following files exceed the 10MB limit:\n${fileNames}\n\nPlease compress or resize these files before uploading.`);
      e.target.value = ''; // Clear the file input
      return;
    }
    
    const newFiles = [...uploadedFiles, ...files];
    setUploadedFiles(newFiles);
    updateFormData({ uploadedFiles: newFiles });
  };

  const handleRemoveFile = (index) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(newFiles);
    updateFormData({ uploadedFiles: newFiles });
  };

  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  const handleSignatureEnd = () => {
    // Signature drawing completed - touch event handling is isolated
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate signature
    if (signatureRef.current && signatureRef.current.isEmpty()) {
      alert('Please provide your signature before submitting.');
      return;
    }

    // Get signature data
    let signatureData = null;
    if (signatureRef.current) {
      try {
        const canvas = signatureRef.current.getCanvas();
        signatureData = canvas.toDataURL('image/png');
        console.log('Signature captured, length:', signatureData?.length);
      } catch (error) {
        console.error('Error getting signature:', error);
        alert('Error capturing signature. Please try again.');
        return;
      }
    }

    if (!signatureData) {
      alert('Please provide your signature before submitting.');
      return;
    }

    // Update form data for record keeping (optional)
    updateFormData({ signature: signatureData });
    
    // Pass signature directly to onSubmit handler
    console.log('Passing signature directly to onSubmit');
    onSubmit(signatureData);
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {t('page6.header.title')}
        </h1>
        <p className="text-gray-600">{t('page6.header.subtitle')}</p>
      </div>

      {/* Disclosure Text */}
      <div className="mb-6 p-6 bg-gray-50 rounded-lg border border-gray-200">
        <div className="text-sm text-gray-800 space-y-4 leading-relaxed">
          <p>
            {t('page6.disclosure.paragraph1', { organizationName: organization?.name || 'Tax Services' })}
          </p>

          <p>
            {t('page6.disclosure.paragraph2', { organizationName: organization?.name || 'Tax Services' })}
          </p>

          <p>
            {t('page6.disclosure.paragraph3')}
          </p>

          <p>
            {t('page6.disclosure.paragraph4', { organizationName: organization?.name || 'Tax Services' })}
          </p>

          <p>
            {t('page6.disclosure.paragraph5', { organizationName: organization?.name || 'Tax Services' })}
          </p>

          <p className="font-semibold">
            {t('page6.disclosure.closing')}
          </p>
        </div>
      </div>

      {/* Taxpayer Name */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-900 mb-3">
          {t('page6.taxpayerName')} <span className="text-red-500">{t('common.required')}</span>
        </label>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <input
              type="text"
              name="disclosureTaxpayerFirstName"
              value={formData.disclosureTaxpayerFirstName}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
            <p className="text-xs text-gray-500 mt-1">{t('common.firstName')}</p>
          </div>
          <div>
            <input
              type="text"
              name="disclosureTaxpayerMiddleName"
              value={formData.disclosureTaxpayerMiddleName}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">{t('common.middleName')}</p>
          </div>
          <div>
            <input
              type="text"
              name="disclosureTaxpayerLastName"
              value={formData.disclosureTaxpayerLastName}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
            <p className="text-xs text-gray-500 mt-1">{t('common.lastName')}</p>
          </div>
        </div>
      </div>

      {/* Signature Section */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-900 mb-3">
          {t('page6.taxpayerSignature')} <span className="text-red-500">{t('common.required')}</span>
        </label>
        <div 
          className="border-2 border-gray-300 rounded-lg bg-white"
          style={{ touchAction: 'none' }}
        >
          <SignatureCanvas
            ref={signatureRef}
            canvasProps={{
              className: 'w-full h-40 cursor-crosshair',
              style: { touchAction: 'none' }
            }}
            onEnd={handleSignatureEnd}
            clearOnResize={false}
          />
        </div>
        <div className="flex justify-between items-center mt-2">
          <p className="text-xs text-gray-500">{t('page6.signaturePoweredBy', { organizationName: organization?.name || 'Tax Services' })}</p>
          <button
            type="button"
            onClick={clearSignature}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            {t('common.clear')}
          </button>
        </div>
      </div>

      {/* Date - Auto-filled with today's date */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-900 mb-2">
          {t('common.date')} <span className="text-red-500">{t('common.required')}</span>
        </label>
        <input
          type="text"
          inputMode="numeric"
          name="signatureDate"
          value={displayDateValue(formData.signatureDate)}
          onChange={handleDateChange}
          placeholder="MM/DD/YYYY"
          maxLength="10"
          className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
          required
          readOnly
        />
        <p className="text-xs text-gray-500 mt-1">Date automatically set to today</p>
      </div>

      {/* File Upload Section */}
      <div className="mb-8">
        <label className="block text-sm font-medium text-gray-900 mb-3">
          {t('page6.uploadLabel')}
        </label>
        <p className="text-xs text-gray-600 mb-3">
          {t('page6.uploadSubtext')}
        </p>
        
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
          <input
            type="file"
            multiple
            onChange={handleFileUpload}
            className="hidden"
            id="file-upload"
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          />
          <label htmlFor="file-upload" className="cursor-pointer">
            <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
              <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <p className="mt-2 text-sm text-gray-600 font-medium">
              {t('page6.uploadButton')}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {t('page6.uploadDragDrop')}
            </p>
          </label>
        </div>
        
        {/* Display Uploaded Files */}
        {uploadedFiles.length > 0 && (
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">{t('page6.uploadedFiles')}</p>
            <ul className="space-y-2">
              {uploadedFiles.map((file, index) => (
                <li key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200">
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-700">{file.name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex gap-4">
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className="w-1/2 bg-gray-200 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-300 transition-colors font-medium text-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('common.back')}
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-1/2 bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 transition-colors font-medium text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isSubmitting ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {t('common.submitting') || 'Submitting...'}
            </>
          ) : (
            t('common.submit')
          )}
        </button>
      </div>
    </form>
  );
}

export default Page6;
